<template>
  <div
    class="navbar-wrapper fixed-top"
    :class="{
      'menu-open': isMenuOpen,
      'navbar-wrapper--home': isHomePage,
    }"
  >
    <nav class="navbar luxury-nav">
      <div class="luxury-nav-inner">
        <div class="luxury-nav-left">
          <button
            class="luxury-icon-btn"
            type="button"
            :aria-expanded="isMenuOpen"
            :aria-label="isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')"
            @click="toggleMenu"
          >
            <svg v-if="!isMenuOpen" class="luxury-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 7H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              <path d="M4 12H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              <path d="M4 17H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <svg v-else class="luxury-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6L18 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              <path d="M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </button>
          <button
            class="luxury-icon-btn"
            type="button"
            :aria-label="t('nav.search')"
            @click="toggleSearchDrawer"
          >
            <svg class="luxury-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M10.77 18.3C9.2807 18.3 7.82485 17.8584 6.58655 17.031C5.34825 16.2036 4.38311 15.0275 3.81318 13.6516C3.24325 12.2757 3.09413 10.7616 3.38468 9.30096C3.67523 7.84029 4.39239 6.49857 5.44548 5.44548C6.49857 4.39239 7.84029 3.67523 9.30096 3.38468C10.7616 3.09413 12.2757 3.24325 13.6516 3.81318C15.0275 4.38311 16.2036 5.34825 17.031 6.58655C17.8584 7.82485 18.3 9.2807 18.3 10.77C18.3 11.7588 18.1052 12.738 17.7268 13.6516C17.3484 14.5652 16.7937 15.3953 16.0945 16.0945C15.3953 16.7937 14.5652 17.3484 13.6516 17.7268C12.738 18.1052 11.7588 18.3 10.77 18.3ZM10.77 4.75C9.58331 4.75 8.42327 5.10189 7.43657 5.76118C6.44988 6.42046 5.68084 7.35754 5.22672 8.45389C4.77259 9.55025 4.65377 10.7566 4.88528 11.9205C5.11679 13.0844 5.68824 14.1535 6.52735 14.9926C7.36647 15.8317 8.43556 16.4032 9.59945 16.6347C10.7633 16.8662 11.9697 16.7474 13.0661 16.2933C14.1624 15.8391 15.0995 15.0701 15.7588 14.0834C16.4181 13.0967 16.77 11.9367 16.77 10.75C16.77 9.15869 16.1379 7.63257 15.0126 6.50735C13.8874 5.38213 12.3613 4.75 10.77 4.75Z" fill="currentColor" />
              <path d="M20 20.75C19.9015 20.7504 19.8038 20.7312 19.7128 20.6934C19.6218 20.6557 19.5392 20.6001 19.47 20.53L15.34 16.4C15.2075 16.2578 15.1354 16.0697 15.1388 15.8754C15.1422 15.6811 15.221 15.4958 15.3584 15.3583C15.4958 15.2209 15.6812 15.1422 15.8755 15.1388C16.0698 15.1354 16.2578 15.2075 16.4 15.34L20.53 19.47C20.6704 19.6106 20.7493 19.8012 20.7493 20C20.7493 20.1987 20.6704 20.3893 20.53 20.53C20.4608 20.6001 20.3782 20.6557 20.2872 20.6934C20.1962 20.7312 20.0985 20.7504 20 20.75Z" fill="currentColor" />
            </svg>
          </button>
        </div>

        <a class="luxury-brand" href="/">
          <img
            :src="logoSrc"
            :alt="`${storeName} Logo`"
            class="luxury-logo img-fluid"
            width="140"
            height="48"
          />
        </a>

        <div class="luxury-nav-right">
          <button
            class="luxury-icon-btn"
            type="button"
            :aria-label="t('nav.myAccount')"
            @click="handleUserAccount"
          >
            <svg class="luxury-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 12.25C11.2583 12.25 10.5333 12.0301 9.91661 11.618C9.29993 11.206 8.81928 10.6203 8.53545 9.93506C8.25163 9.24984 8.17736 8.49584 8.32206 7.76841C8.46675 7.04098 8.8239 6.3728 9.34835 5.84835C9.8728 5.3239 10.541 4.96675 11.2684 4.82206C11.9958 4.67736 12.7498 4.75162 13.4351 5.03545C14.1203 5.31928 14.706 5.79993 15.118 6.41661C15.5301 7.0333 15.75 7.75832 15.75 8.5C15.75 9.49456 15.3549 10.4484 14.6517 11.1517C13.9484 11.8549 12.9946 12.25 12 12.25ZM12 6.25C11.555 6.25 11.12 6.38196 10.75 6.62919C10.38 6.87643 10.0916 7.22783 9.92127 7.63896C9.75098 8.0501 9.70642 8.5025 9.79323 8.93895C9.88005 9.37541 10.0943 9.77632 10.409 10.091C10.7237 10.4057 11.1246 10.62 11.561 10.7068C11.9975 10.7936 12.4499 10.749 12.861 10.5787C13.2722 10.4084 13.6236 10.12 13.8708 9.75003C14.118 9.38002 14.25 8.94501 14.25 8.5C14.25 7.90326 14.0129 7.33097 13.591 6.90901C13.169 6.48705 12.5967 6.25 12 6.25Z" fill="currentColor" />
              <path d="M19 19.25C18.8019 19.2474 18.6126 19.1676 18.4725 19.0275C18.3324 18.8874 18.2526 18.6981 18.25 18.5C18.25 16.55 17.19 15.25 12 15.25C6.81 15.25 5.75 16.55 5.75 18.5C5.75 18.6989 5.67098 18.8897 5.53033 19.0303C5.38968 19.171 5.19891 19.25 5 19.25C4.80109 19.25 4.61032 19.171 4.46967 19.0303C4.32902 18.8897 4.25 18.6989 4.25 18.5C4.25 13.75 9.68 13.75 12 13.75C14.32 13.75 19.75 13.75 19.75 18.5C19.7474 18.6981 19.6676 18.8874 19.5275 19.0275C19.3874 19.1676 19.1981 19.2474 19 19.25Z" fill="currentColor" />
            </svg>
          </button>
          <button
            class="luxury-icon-btn luxury-cart-btn"
            type="button"
            :aria-label="t('nav.viewCart')"
            @click="toggleMiniCart"
          >
            <ShoppingBagIcon icon-class="luxury-icon" />
            <span v-if="cartCount > 0" class="luxury-cart-count">{{ cartCount }}</span>
          </button>
        </div>
      </div>
    </nav>

    <aside class="luxury-sidebar" :class="{ show: isMenuOpen }">
      <nav class="luxury-sidebar-nav accordion" id="luxuryMenuAccordion">
        <div v-if="isLoadingCategories && !hasConfiguredNavbarLinks" class="luxury-sidebar-skeleton">
          <div v-for="index in 6" :key="index" class="luxury-skeleton-line"></div>
        </div>

        <template v-else>
          <template v-for="item in visibleNavbarLinks" :key="item.id">
            <div v-if="item.children?.length" class="accordion-item border-0">
              <h2 class="accordion-header" :id="'luxuryHeading' + item.id">
                <button
                  class="accordion-button collapsed luxury-nav-link luxury-nav-link--toggle"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="'#luxuryCollapse' + item.id"
                >
                  <Pickable
                    v-if="isPickMode && isConfiguredNavLink(item)"
                    tag="span"
                    :label="'Navbar · ' + (item.href || 'enlace')"
                    @pick="pickNavLink(item)"
                  >
                    {{ item.label }}
                  </Pickable>
                  <template v-else>{{ item.label }}</template>
                </button>
              </h2>
              <div
                :id="'luxuryCollapse' + item.id"
                class="accordion-collapse collapse"
                :aria-labelledby="'luxuryHeading' + item.id"
                data-bs-parent="#luxuryMenuAccordion"
              >
                <div class="luxury-submenu">
                  <a
                    :href="item.href"
                    class="luxury-submenu-link luxury-submenu-link--parent"
                    @click="closeMenu"
                  >
                    {{ t('nav.viewAll') }}
                  </a>
                  <a
                    v-for="child in item.children"
                    :key="child.id"
                    :href="child.href"
                    class="luxury-submenu-link"
                    @click="closeMenu"
                  >
                    <Pickable
                      v-if="isPickMode && isConfiguredChildLink(item, child)"
                      tag="span"
                      :label="'Navbar · ' + (child.href || 'subenlace')"
                      @pick="pickChildLink(item, child)"
                    >
                      {{ child.label }}
                    </Pickable>
                    <template v-else>{{ child.label }}</template>
                  </a>
                </div>
              </div>
            </div>
            <a
              v-else
              :href="item.href"
              class="luxury-nav-link"
              @click="closeMenu"
            >
              <Pickable
                v-if="isPickMode && isConfiguredNavLink(item)"
                tag="span"
                :label="'Navbar · ' + (item.href || 'enlace')"
                @pick="pickNavLink(item)"
              >
                {{ item.label }}
              </Pickable>
              <template v-else>{{ item.label }}</template>
            </a>
          </template>
        </template>
      </nav>

      <div class="luxury-sidebar-footer">
        <button type="button" class="luxury-footer-link luxury-footer-link--button" @click="handleUserAccount">
          <svg class="luxury-footer-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="8" r="3.5" stroke="currentColor" stroke-width="1.25" />
            <path d="M5 19.5C5.8 16.2 8.6 14.5 12 14.5C15.4 14.5 18.2 16.2 19 19.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square" />
          </svg>
          {{ t('nav.account') }}
        </button>

        <div class="luxury-language" :aria-label="t('language.select')">
          <button
            type="button"
            class="luxury-lang-btn"
            :class="{ 'is-active': locale === 'es' }"
            :aria-pressed="locale === 'es'"
            @click="setLocale('es')"
          >
            ESP
          </button>
          <span class="luxury-lang-sep">/</span>
          <button
            type="button"
            class="luxury-lang-btn"
            :class="{ 'is-active': locale === 'en' }"
            :aria-pressed="locale === 'en'"
            @click="setLocale('en')"
          >
            ENG
          </button>
        </div>

        <p class="luxury-shipping-note">
          {{ t('nav.shippingSpain') }} / EUR €
        </p>
      </div>
    </aside>

    <div class="luxury-sidebar-backdrop" :class="{ show: isMenuOpen }" @click="closeMenu"></div>

    <!-- Search Drawer -->
    <SearchDrawer :is-open="isSearchDrawerOpen" @close="closeSearchDrawer" />
    <!-- Mini Cart Drawer -->
    <MiniCartDrawer :is-open="isMiniCartOpen" @close="closeMiniCart" />
  </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { getCategories } from '@/services/categories';
import { getCollections } from '@/modules/catalog/services/productApi';
import MiniCartDrawer from '@/modules/cart/components/MiniCartDrawer.vue';
import SearchDrawer from '@/modules/catalog/components/CatalogSearchDrawer.vue';
import ShoppingBagIcon from '@/components/icons/ShoppingBagIcon.vue';
import { useSiteContact } from '@/composables/useSiteContact';
import { useStorefrontTheme } from '@/composables/useStorefrontTheme';
import { useI18n } from '@/composables/useI18n';
import { localizedCategoryName } from '@/utils/translations';
import { isPickMode, pickElement } from '@/composables/usePickMode';
import Pickable from '@/modules/admin/ui/PickableItem.vue';
import brand from '@/config/brand';

export default {
  name: "AppNavbar",
  components: { MiniCartDrawer, SearchDrawer, ShoppingBagIcon, Pickable },
  props: {
    accentModeOverride: {
      type: String,
      default: '',
    },
    themeConfigOverride: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      searchQuery: "",
      activeDropdown: null,
      isMenuOpen: false,
      isMiniCartOpen: false,
      isSearchDrawerOpen: false,
      categories: [],
      isLoadingCategories: false,
      categoryError: null,
      collections: [],
      isLoadingCollections: false,
    };
  },
  created() {
    // Mejorar el logging para depuración
    this.loadCategories();
    this.loadCollections();
  },
  setup(props) {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const cartCount = computed(() => store.getters.cartCount);
    const username = ref("");
    const isAdmin = ref(false);
    const isDistributor = ref(false);
    const isAuthenticated = ref(false);

    const updateUserInfo = () => {
      const token = localStorage.getItem("accessToken");
      isAuthenticated.value = !!token;
      isAdmin.value = localStorage.getItem("isAdmin") === "true";
      isDistributor.value = localStorage.getItem("customerType") === "DISTRIBUTOR";

      if (isAuthenticated.value) {
        const firstName = localStorage.getItem("userFirstName");
        const lastName = localStorage.getItem("userLastName");
        if (firstName && lastName) {
          username.value = `${firstName} ${lastName}`;
        } else {
          username.value = localStorage.getItem("username") || "Usuario";
        }
      } else {
        username.value = "";
      }
    };

    onMounted(() => {
      updateUserInfo();
      window.addEventListener("storage", updateUserInfo);
    });

    // Limpiar el event listener cuando el componente se desmonta
    onUnmounted(() => {
      window.removeEventListener("storage", updateUserInfo);
    });

    const {
      contactMailto,
      whatsappHref,
      instagramUrl,
      storeName,
      navbarLinks,
      socialTiktokUrl,
      socialYoutubeUrl,
    } = useSiteContact();

    const { locale, t, setLocale } = useI18n();
    const { logoSrc } = useStorefrontTheme({
      accentMode: computed(() => props.accentModeOverride || null),
      config: computed(() => props.themeConfigOverride || null),
    });

    return {
      cartCount,
      router,
      route,
      username,
      isAdmin,
      isDistributor,
      isAuthenticated,
      contactMailto,
      whatsappHref,
      instagramUrl,
      storeName,
      navbarLinks,
      socialTiktokUrl,
      socialYoutubeUrl,
      enableBlog: brand.enableBlog,
      // ── Modo selección (elegir elemento de la web para editar en el panel) ──
      isPickMode,
      isConfiguredNavLink: (item) => {
        if (!item || !item.id) return false
        const configured = Array.isArray(navbarLinks.value) ? navbarLinks.value : []
        return configured.some((l) => String(l.id) === String(item.id))
      },
      isConfiguredChildLink: (parent, child) => {
        if (!parent || !child || !parent.id || !child.id) return false
        const configured = Array.isArray(navbarLinks.value) ? navbarLinks.value : []
        const parentItem = configured.find((l) => String(l.id) === String(parent.id))
        return Boolean(parentItem && Array.isArray(parentItem.children)
          && parentItem.children.some((c) => String(c.id) === String(child.id)))
      },
      pickNavLink: (item) => pickElement({ tab: 'navbar', field: `tr-nav-${item.id}` }),
      pickChildLink: (parent, child) => pickElement({ tab: 'navbar', field: `tr-nav-${parent.id}-${child.id}` }),
      logoSrc,
      locale,
      t,
      setLocale,
    };
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    this.updateNavbarHeightVariables();
    window.addEventListener("resize", this.updateNavbarHeightVariables);

    // Initialize Bootstrap components if needed
    this.initBootstrapComponents();
  },
  unmounted() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("resize", this.updateNavbarHeightVariables);
  },
  computed: {
    isHomePage() {
      return this.route?.path === '/';
    },
    parentCategories() {
      return this.categories.filter(cat => !cat.parent);
    },
    hasConfiguredNavbarLinks() {
      return Array.isArray(this.navbarLinks) && this.navbarLinks.length > 0;
    },
    visibleNavbarLinks() {
      const configured = Array.isArray(this.navbarLinks) ? this.navbarLinks : [];
      const categoryLinks = this.categoryNavbarLinks();
      const configuredSource = configured.length ? configured : this.baseNavbarLinks();
      const configuredHrefs = new Set(
        configuredSource
          .filter((item) => item && item.is_active !== false)
          .map((item) => this.normalizeNavbarHref(item.href))
          .filter(Boolean)
      );
      const missingCategoryLinks = categoryLinks.filter((item) => (
        !configuredHrefs.has(this.normalizeNavbarHref(item.href))
      ));
      const source = [...configuredSource, ...missingCategoryLinks, ...this.collectionNavbarLinks()];

      return source
        .filter((item) => item && item.is_active !== false && item.label && item.href)
        .map((item, index) => ({
          id: item.id || `nav-${index + 1}`,
          label: this.normalizeNavbarLabel(item),
          href: item.href,
          children: Array.isArray(item.children)
            ? item.children.filter((child) => child && child.is_active !== false && child.label && child.href)
            : [],
        }));
    },
  },
  watch: {
    $route() {
      this.closeMenu();
    },
    isMenuOpen() {
      document.body.style.overflow = this.isMenuOpen ? 'hidden' : '';
    },
  },
  methods: {
    initBootstrapComponents() {
      // Initialize Bootstrap accordion if not using data attributes
      if (window.bootstrap && window.bootstrap.Collapse) {
        const accordionItems = document.querySelectorAll(".accordion-collapse");
        accordionItems.forEach((item) => {
          new window.bootstrap.Collapse(item, { toggle: false });
        });
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    closeMenu() {
      if (!this.isMenuOpen) return;
      this.isMenuOpen = false;
      document.body.style.overflow = '';

      const accordions = document.querySelectorAll('.accordion-collapse.show');
      accordions.forEach((accordion) => {
        if (window.bootstrap && window.bootstrap.Collapse) {
          const bsCollapse = window.bootstrap.Collapse.getInstance(accordion) || new window.bootstrap.Collapse(accordion);
          bsCollapse.hide();
        }
      });
    },

    toggleSearchDrawer() {
      this.isSearchDrawerOpen = !this.isSearchDrawerOpen;
    },

    closeSearchDrawer() {
      this.isSearchDrawerOpen = false;
    },
    updateNavbarHeightVariables() {
      // Get navbar wrapper element
      const navbarWrapper = document.querySelector(".navbar-wrapper");
      if (navbarWrapper) {
        // Calculate navbar height
        const navbarHeight = navbarWrapper.offsetHeight;

        // Set CSS variables
        document.documentElement.style.setProperty(
          "--navbar-total-height",
          `${navbarHeight}px`
        );

        // Also set a mobile version if screen is smaller
        if (window.innerWidth <= 991.98) {
          document.documentElement.style.setProperty(
            "--navbar-total-height-mobile",
            `${navbarHeight}px`
          );

          // Set optimized hero height for mobile based on screen height
          const screenHeight = window.innerHeight;
          let optimizedHeroHeight;

          if (window.innerWidth <= 480) {
            optimizedHeroHeight = Math.max(screenHeight * 0.6, 350); // 60% of screen or at least 350px
          } else if (window.innerWidth <= 768) {
            optimizedHeroHeight = Math.max(screenHeight * 0.7, 400); // 70% of screen or at least 400px
          } else {
            optimizedHeroHeight = Math.max(screenHeight * 0.7, 450); // 70% of screen or at least 450px
          }

          document.documentElement.style.setProperty(
            "--hero-height",
            `${optimizedHeroHeight}px`
          );

          // Set a background color that matches the products section
          document.documentElement.style.setProperty(
            "--hero-bg-color",
            "#f8f8f8"
          );
        } else {
          // Reset hero height for desktop
          document.documentElement.style.removeProperty("--hero-height");
          document.documentElement.style.removeProperty("--hero-bg-color");
        }
      }
    },
    handleResize() {
      this.updateNavbarHeightVariables();
    },
    handleSearch() {
      if (this.searchQuery.trim()) {
        this.router.push({
          path: "/productos",
          query: { search: this.searchQuery.trim() },
        });
        this.closeMenu();
      }
    },
    categoryHref(categoryName) {
      return `/productos?categories=${encodeURIComponent(categoryName)}`;
    },
    resolveCategoryName(category) {
      return localizedCategoryName(category, this.locale);
    },
    normalizeNavbarHref(href) {
      return String(href || '').trim();
    },
    normalizeNavbarLabel(item) {
      if (this.normalizeNavbarHref(item?.href) === '/productos?on_sale=true') {
        return this.locale === 'en' ? 'Offers' : 'Ofertas / promociones';
      }
      return item.label;
    },
    baseNavbarLinks() {
      return [
        { id: 'nav-collection', label: this.t('nav.products'), href: '/productos', children: [] },
        { id: 'nav-new', label: this.t('nav.news'), href: '/productos?on_sale=true', children: [] },
        { id: 'nav-contact', label: this.t('nav.contact'), href: '/contacto', children: [] },
      ];
    },
    categoryNavbarLinks() {
      return this.parentCategories.map((category) => ({
        id: `category-${category.id}`,
        label: localizedCategoryName(category, this.locale),
        href: this.categoryHref(category.name),
        children: this.getDirectSubcategories(category.id).map((subcat) => ({
          id: `category-${subcat.id}`,
          label: localizedCategoryName(subcat, this.locale),
          href: this.categoryHref(subcat.name),
        })),
      }));
    },
    collectionHref(collection) {
      return `/productos?collection_id=${collection.id}&collection_name=${encodeURIComponent(collection.name)}`;
    },
    collectionNavbarLinks() {
      if (!Array.isArray(this.collections) || !this.collections.length) {
        return [];
      }
      return [
        {
          id: 'nav-collections',
          label: this.t('nav.collections'),
          href: '/productos',
          children: this.collections.map((collection) => ({
            id: `collection-${collection.id}`,
            label: collection.name,
            href: this.collectionHref(collection),
          })),
        },
      ];
    },
    async loadCollections() {
      try {
        this.isLoadingCollections = true;
        const response = await getCollections();
        this.collections = Array.isArray(response) ? response : (response?.results || []);
      } catch (error) {
        console.error('Error loading collections:', error);
      } finally {
        this.isLoadingCollections = false;
      }
    },
    defaultNavbarLinks() {
      return [...this.baseNavbarLinks(), ...this.categoryNavbarLinks()];
    },
    handleUserAccount() {
      if (this.isAuthenticated) {
        if (this.isAdmin) {
          this.router.push("/configuracion/productos");
        } else {
          this.router.push("/account/orders");
        }
      } else {
        this.router.push("/iniciar-sesion");
      }
      this.closeMenu();
    },

    async loadCategories() {
      try {
        this.isLoadingCategories = true;

        const response = await getCategories();
        this.categories = response.results || response;

        // Process categories and add slugs if missing
        this.categories = this.categories.map(category => ({
          ...category,
          slug: category.slug || this.generateSlug(category.name)
        }));

      } catch (error) {
        console.error('Error loading categories:', error);
        this.categoryError = 'Error al cargar las categorías';
      } finally {
        this.isLoadingCategories = false;
      }
    },

    // Generate slug client side if needed
    generateSlug(name) {
      return name
        .toLowerCase()
        .replace(/\s+/g, '-')           // Replace spaces with -
        .replace(/[^\w-]+/g, '')        // Remove all non-word chars
        .replace(/--+/g, '-')           // Replace multiple - with single -
        .replace(/^-+/, '')             // Trim - from start of text
        .replace(/-+$/, '');            // Trim - from end of text
    },

    // Esta función es crítica para comprobar si una categoría tiene subcategorías
    hasSubcategories(categoryId) {
      const hasChildren = this.categories.some(cat => cat.parent === categoryId);
      return hasChildren;
    },

    // Asegurar que estas funciones funcionan correctamente
    getDirectSubcategories(parentId) {
      const subcats = this.categories.filter(cat => cat.parent === parentId);
      return subcats;
    },

    // Método mejorado para obtener subcategorías anidadas
    getNestedSubcategories(parentId) {
      // Obtener las subcategorías directas
      const directSubcats = this.getDirectSubcategories(parentId);

      // Para cada subcategoría directa, agregar sus propias subcategorías
      return directSubcats.map(subcat => ({
        ...subcat,
        children: this.categories.filter(cat => cat.parent === subcat.id)
      }));
    },

    toggleMiniCart() {
      this.isMiniCartOpen = !this.isMiniCartOpen;
    },
    closeMiniCart() {
      this.isMiniCartOpen = false;
    },
  },
};
</script>

<style scoped>
/* ===== LUXURY NAVBAR ===== */
.navbar-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1050;
  background: transparent !important;
  box-shadow: none !important;
  transition: color 0.3s ease;
}

.navbar-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: clamp(50px, 14vh, 160px);
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.78) 0%,
    rgba(0, 0, 0, 0.68) 52%,
    rgba(0, 0, 0, 0.48) 68%,
    rgba(0, 0, 0, 0.15) 82%,
    transparent 100%
  );
  pointer-events: none;
  z-index: 0;
  transition: opacity 0.3s ease;
}

.navbar-wrapper.menu-open {
  background: transparent !important;
  box-shadow: none !important;
}

.navbar-wrapper.menu-open::before {
  opacity: 0;
}

.luxury-nav {
  position: relative;
  z-index: 1;
  padding: 0;
  width: 100%;
  background: transparent !important;
  box-shadow: none !important;
}

.luxury-nav-inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  column-gap: 1rem;
  width: 100%;
  min-height: 72px;
  padding: 0 clamp(1.25rem, 3vw, 2.5rem);
}

.luxury-nav-left {
  grid-column: 1;
  justify-self: start;
}

.luxury-brand {
  grid-column: 2;
  justify-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.luxury-nav-right {
  grid-column: 3;
  justify-self: end;
}

.luxury-nav-left,
.luxury-nav-right {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
}

.luxury-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none !important;
  outline: none;
  box-shadow: none !important;
  background: transparent;
  color: var(--navbar-accent) !important;
  cursor: pointer;
  flex-shrink: 0;
  appearance: none;
  -webkit-appearance: none;
  transition: opacity 0.2s ease;
}

.luxury-icon-btn:focus,
.luxury-icon-btn:focus-visible {
  outline: none;
  box-shadow: none !important;
}

.luxury-icon-btn:hover {
  opacity: 0.65;
}

.luxury-icon {
  width: 22px;
  height: 22px;
  display: block;
}

.luxury-logo {
  width: clamp(130px, 12vw, 130px);
  height: auto;
  display: block;
  object-fit: contain;
  object-position: center;
  transition: opacity 0.2s ease;
  margin-bottom: 10px;
}

.luxury-brand:hover .luxury-logo {
  opacity: 0.85;
}

@keyframes luxury-nav-fade-down {
  from {
    opacity: 0;
    transform: translate3d(0, -18px, 0);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.navbar-wrapper--home .luxury-nav-left {
  animation: luxury-nav-fade-down 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both;
}

.navbar-wrapper--home .luxury-brand {
  animation: luxury-nav-fade-down 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.25s both;
}

.navbar-wrapper--home .luxury-nav-right {
  animation: luxury-nav-fade-down 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.35s both;
}

@media (prefers-reduced-motion: reduce) {
  .navbar-wrapper--home .luxury-nav-left,
  .navbar-wrapper--home .luxury-brand,
  .navbar-wrapper--home .luxury-nav-right {
    animation: none;
  }
}

.luxury-cart-btn {
  position: relative;
}

.luxury-cart-count {
  position: absolute;
  top: -2px;
  right: -4px;
  min-width: 12px;
  height: 12px;
  padding: 0 3px;
  border-radius: 0;
  background: #ffffff;
  color: #000000;
  font-size: 0.55rem;
  font-weight: 600;
  line-height: 12px;
  text-align: center;
}

.luxury-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: min(320px, 82vw);
  height: 100vh;
  height: 100dvh;
  z-index: 1060;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.821);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  transform: translateX(-100%);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  padding: calc(72px + 1.5rem) 2rem 1.5rem;
}

.luxury-sidebar.show {
  transform: translateX(0);
}

.luxury-sidebar-nav {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.luxury-nav-link {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.55rem 0;
  border: 0;
  background: transparent;
  color: var(--black-color) !important;
  font-family: 'Host Grotesk', sans-serif;
  font-size: 0.78rem;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-decoration: none;
  text-align: left;
  transition: opacity 0.2s ease;
}

.luxury-nav-link:hover {
  opacity: 0.55;
}

.luxury-nav-link--toggle::after {
  margin-left: auto;
}

.luxury-sidebar .accordion-item {
  background: transparent;
  border: none !important;
}

.luxury-sidebar .accordion-button {
  padding: 0.55rem 0;
  box-shadow: none !important;
  border: 0 !important;
  border-radius: 0 !important;
  font-weight: 300 !important;
}

.luxury-sidebar .accordion-button::after {
  width: 0.75rem;
  height: 0.75rem;
  background-size: 0.75rem;
  opacity: 0.45;
}

.luxury-sidebar .accordion-button:not(.collapsed) {
  background: transparent !important;
  color: var(--black-color) !important;
}

.luxury-submenu {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.25rem 0 0.75rem 0.75rem;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  margin-left: 0.15rem;
}

.luxury-submenu-link {
  color: var(--black-color) !important;
  font-family: 'Host Grotesk', sans-serif;
  font-size: 0.72rem;
  font-weight: 300;
  letter-spacing: 0.08em;
  text-decoration: none;
  text-transform: uppercase;
  transition: color 0.2s ease;
}

.luxury-submenu-link:hover {
  color: var(--black-color) !important;
}

.luxury-submenu-link--parent {
  font-weight: 500;
  opacity: 0.72;
}

.luxury-sidebar-footer {
  margin-top: auto;
  padding-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.luxury-footer-link {
  color: var(--black-color);
  font-family: 'Host Grotesk', sans-serif;
  font-size: 0.68rem;
  font-weight: 300;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  transition: color 0.2s ease;
}

.luxury-footer-link:hover {
  color: var(--black-color);
}

.luxury-footer-link--button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.luxury-footer-icon {
  width: 14px;
  height: 14px;
}

.luxury-shipping-note {
  margin: 0;
  color: var(--black-color);
  font-family: 'Host Grotesk', sans-serif;
  font-size: 0.68rem;
  font-weight: 300;
  letter-spacing: 0.06em;
}

.luxury-language {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--black-color);
  font-family: 'Host Grotesk', sans-serif;
  font-size: 0.68rem;
  font-weight: 300;
  letter-spacing: 0.12em;
}

.luxury-lang-btn {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--black-color);
  font-family: inherit;
  font-size: inherit;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.luxury-lang-btn:hover {
  opacity: 0.9;
}

.luxury-lang-btn.is-active {
  opacity: 1;
  font-weight: 700;
}

.luxury-lang-sep {
  opacity: 0.45;
}

.luxury-sidebar-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.luxury-skeleton-line {
  height: 14px;
  background: rgba(0, 0, 0, 0.08);
  animation: pulse 1.5s infinite ease-in-out;
}

.luxury-sidebar-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  background: rgba(0, 0, 0, 0.28);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.35s ease, visibility 0.35s ease;
}

.luxury-sidebar-backdrop.show {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

@media (max-width: 767.98px) {
  .luxury-nav-inner {
    min-height: 64px;
  }

  .luxury-nav-left,
  .luxury-nav-right {
    gap: 0.85rem;
  }

  .luxury-brand {
    letter-spacing: 0.24em;
  }

  .luxury-sidebar {
    padding-top: calc(64px + 1.25rem);
  }
}

.social-links {
  display: flex;
  gap: 15px;
}

.social-link {
  color: var(--text-dark);
  text-decoration: none;
  transition: opacity 0.3s ease;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.social-link:hover {
  opacity: 0.8;
  background-color: rgba(255, 255, 255, 0.2);
}

.timer-container {
  display: flex;
  align-items: center;
}

.timer-box {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 2px 6px;
  margin: 0 4px;
  font-weight: bold;
}

/* Estilos del menú superior */
.top-menu {
  background-color: #f8f9fa;
  padding: 8px 20px;
  font-size: 13px;
}

.top-menu-links {
  display: flex;
  align-items: center;
}

.top-menu-link {
  color: #6c757d;
  margin-right: 15px;
  text-decoration: none;
}

.top-menu-link:hover {
  color: #6a42b9;
}

.delivery-time {
  color: #6c757d;
  margin-left: 10px;
}

.highlight {
  color: #ff6b6b;
  font-weight: bold;
}

.top-menu-right {
  display: flex;
  align-items: center;
}

.language-selector,
.currency-selector {
  margin-right: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.language-selector i,
.currency-selector i {
  font-size: 10px;
  margin-left: 5px;
}

.tracking-link {
  color: #6c757d;
  text-decoration: none;
}

/* Legacy navbar styles removed ´see luxury navbar block above */

.navbar-scrolled {
  background-color: rgb(0, 0, 0) !important;
  color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.navbar-svg-icon {
  transition: color 0.3s ease;
  display: block;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
}
.nav-link{
  font-weight: 500;
}
/* Cambios de color cuando se hace scroll */
.navbar-scrolled .navbar-brand,
.navbar-scrolled .user-account,
.navbar-scrolled .account-label,
.navbar-scrolled .nav-actions i,
.navbar-scrolled .nav-actions .navbar-svg-icon,
.navbar-scrolled .mobile-actions i,
.navbar-scrolled .nav-link {
  color: #ffffff !important;
}
.nav-link:hover {
  opacity: .8;
  background: none;
}

.navbar-scrolled .desktop-search-btn .navbar-svg-icon {
  color: #ffffff !important;
}
.account-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.account-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: #fff;
}

.brand-container {
  display: flex;
  align-items: center;
}

.logo-img {
  width: 180px;
  height: auto;
  margin-right: 8px;
}

.brand-name {
  font-size: 20px;
  font-weight: bold;
  color: var(--navbar-text);
}

.location-selector {
  display: flex;
  align-items: center;
  margin-left: 15px;
  font-size: 13px;
  color: var(--navbar-icon);
  cursor: pointer;
  border-left: 1px solid var(--navbar-border);
  padding-left: 15px;
}

.location-selector i {
  margin-right: 5px;
  color: var(--black-color);
}

.location-text {
  font-weight: bold;
  margin: 0 5px;
}

/* Barra de búsqueda */
.search-container {
  flex-grow: 1;
  max-width: 500px;
  margin: 0 30px;
}

.search-input {
  border-radius: 20px 0 0 20px;
  border: 1px solid var(--navbar-border);
  padding-left: 15px;
  height: 42px;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.search-btn {
  background-color: var(--black-color) !important;
  /* Forzar el color */
  color: var(--white-color) !important;
  border-radius: 0 20px 20px 0;
  border: none;
  width: 50px;
  transition: background-color 0.3s ease;
}

.search-btn:hover {
  background-color: var(--black-color) !important;
}

/* Estilo para botón de búsqueda en navbar oscuro */
.navbar-scrolled .search-btn {
  background-color: var(--white-color) !important;
}

/* iconos de acciones */
.nav-actions {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-left: 2rem;
}

/* Transiciones para los iconos de acciones */
.nav-actions i,
.user-account,
.cart-icon,
.account-label,
.account-text {
  transition: color 0.3s ease;
}

.user-account {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.user-account:hover {
  opacity: 0.8;
}

.account-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.account-label {
  font-weight: 500;
  font-size: 0.9rem;
  color: var(--text-dark);
}

.account-role {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 12px;
}

.wishlist-icon,
.cart-icon {
  position: relative;
  margin-left: 20px;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  text-decoration: none;
}

.wishlist-icon i,
.cart-icon i {
  font-size: 22px;
  color: var(--navbar-icon);
}

.wishlist-icon:hover,
.cart-icon:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.wishlist-count,
.cart-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: var(--primary-color) !important;
  /* Cambiado a color pastel */
  color: var(--white-color);
  font-size: 10px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

/* Menu de categorias */
.category-menu {
  background-color: var(--navbar-bg);
  padding: 0 20px;
  position: relative;
  overflow: visible;
  transition: background-color 0.3s ease, color 0.3s ease;
}


.navbar-scrolled+.category-menu {
  background-color: #000;
}

.navbar-scrolled+.category-menu .category-item a {
  color: #fff;
}

.navbar-scrolled+.category-menu .category-item a:hover {
  color: var(--primary-color);
}

.category-list {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
}

.category-item {
  margin-right: 25px;
  position: relative;
}

.category-item a {
  display: block;
  padding: 15px 0;
  color: var(--navbar-text);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.85rem;
  text-transform: capitalize;
}

.category-item a:hover {
  color: var(--primary-color);
}

/* Mejoras a los estilos del dropdown */
.mega-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: max-content;
  min-width: 400px;
  max-width: 800px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1000;
  padding: 20px;
  transform: translateY(10px);
  max-height: 80vh;
  overflow-y: auto;
}

.mega-dropdown.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-toggle .fa-chevron-down {
  font-size: 0.7rem;
  transition: transform 0.3s ease;
}

.dropdown-toggle::after {
  display: none;
}

.has-dropdown:hover .dropdown-toggle .fa-chevron-down {
  transform: rotate(-180deg);
}

.subcategories-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  width: 100%;
}

.subcategory-column {
  padding: 6px;
}

.subcategory-title {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: var(--text-dark);
  text-decoration: none;
  padding: 10px;
  border-radius: 6px;
  transition: all 0.2s ease;
  background-color: rgba(0, 0, 0, 0.04);
  margin-bottom: 0;
  width: 100%;
  white-space: normal;
  line-height: 1.2;
}

.subcategory-title:hover {
  background-color: rgba(0, 0, 0, 0.08);
  color: var(--primary-color);
  transform: translateX(5px);
}

.all-products {
  color: var(--primary-color) !important;
  font-weight: 600 !important;
}

/* Mobile Improvements */
@media (max-width: 991.98px) {
  .mega-dropdown {
    position: static;
    transform: none;
    box-shadow: none;
    padding: 0;
    max-width: 100%;
  }

  .mega-content {
    grid-template-columns: 1fr;
  }

  .category-section {
    padding: 12px;
    background: none;
  }

  .subcategories-grid {
    gap: 12px;
  }

  .category-main-title {
    font-size: 1rem;
    margin-bottom: 12px;
  }

  .subcategory-title {
    font-size: 0.9rem;
  }

  .subcategory-item {
    font-size: 0.85rem;
  }
}

/* Mobile menu and other existing styles */
.mobile-menu-toggle,
.mobile-icon-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  width: 30px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.mobile-icon-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.navbar-scrolled .mobile-icon-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.navbar-scrolled .mobile-icon-btn i,
.navbar-scrolled .mobile-icon-btn .navbar-svg-icon {
  color: #000 !important;
}

.mobile-menu-toggle i.fa-times {
  transform: rotate(0deg);
}


/* Enhanced mobile menu appearance */
@media (max-width: 991.98px) {
  .navbar {
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .logo-img {
    width: 120px;
  }

  .navbar-brand {
    margin-right: 0;
  }

  .mobile-actions {
    gap: 5px;
  }
}

/* ===== OFFCANVAS MENU (MOBILE) ===== */
.offcanvas-menu {
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 360px;
  height: 100vh;
  background-color: #fff;
  z-index: 1050;
  overflow-y: auto;
  transform: translateX(100%);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
}

.offcanvas-menu.show {
  transform: translateX(0);
}

.offcanvas-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  z-index: 1045;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.35s ease;
  pointer-events: none;
}

.offcanvas-backdrop.show {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.offcanvas-header {
  padding: 1.25rem 1.25rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.offcanvas-header .offcanvas-title {
  color: #111;
  font-weight: 700;
  font-size: 1.1rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0;
}

.offcanvas-header .btn-close {
  filter: none;
  opacity: 0.5;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
}

.offcanvas-header .btn-close:hover {
  opacity: 1;
}

.offcanvas-body {
  padding: 1.25rem;
  overflow-y: auto;
  flex: 1;
}

.offcanvas-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

/* Mobile user account */
.mobile-user-account {
  margin-bottom: 1.25rem !important;
}

.mobile-user-account a {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #111 !important;
  background-color: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 0.85rem 1rem;
  transition: background-color 0.2s ease;
}

.mobile-user-account a:hover,
.mobile-user-account a:active {
  background-color: rgba(0, 0, 0, 0.07);
}

.mobile-user-account i.fas {
  font-size: 1.1rem;
  color: var(--primary-color) !important;
}

.mobile-user-account .text-muted {
  color: rgba(0,0,0,0.4) !important;
  font-size: 0.75rem;
}

.mobile-user-account .fa-chevron-right {
  color: rgba(0, 0, 0, 0.25) !important;
  font-size: 0.75rem;
}

/* Mobile search inside offcanvas */
.offcanvas-body .search-container {
  max-width: 100%;
  margin: 0 0 1.25rem;
}

.offcanvas-body .search-input {
  background-color: #f5f5f5 !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  color: #111 !important;
  border-radius: 8px 0 0 8px;
  height: 42px;
}

.offcanvas-body .search-input::placeholder {
  color: rgba(0, 0, 0, 0.35);
}

.offcanvas-body .search-input:focus {
  background-color: #fff !important;
  border-color: var(--primary-color) !important;
  box-shadow: none;
  outline: none;
}

.offcanvas-body .search-btn {
  background-color: var(--primary-color) !important;
  color: #fff !important;
  border-radius: 0 8px 8px 0;
  border: none;
  width: 46px;
}

/* Mobile nav links */
.mobile-link {
  display: flex;
  align-items: center;
  padding: 0.75rem 0.9rem;
  color: #111 !important;
  text-decoration: none !important;
  font-weight: 500;
  font-size: 0.85rem;
  letter-spacing: 0.03em;
  border-radius: 8px;
  transition: background-color 0.2s ease, color 0.2s ease;
  width: 100%;
}

.mobile-link:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #000 !important;
}

.mobile-menu-item {
  margin-bottom: 2px;
}

/* Mobile accordion (categories with subcategories) */
.offcanvas-menu .accordion-item {
  background: transparent;
  border: none !important;
  margin-bottom: 2px;
}

.offcanvas-menu .accordion-button {
  background-color: transparent !important;
  color: #111 !important;
  font-weight: 500;
  font-size: 0.85rem;
  letter-spacing: 0.03em;
  padding: 0.75rem 0.9rem;
  border-radius: 8px !important;
  box-shadow: none !important;
  border: none !important;
  width: 100%;
}

.offcanvas-menu .accordion-button:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
  color: #000 !important;
}

.offcanvas-menu .accordion-button:not(.collapsed) {
  background-color: rgba(0, 0, 0, 0.05) !important;
  color: var(--primary-color) !important;
}

.offcanvas-menu .accordion-button::after {
  filter: none;
  opacity: 0.5;
}

.offcanvas-menu .accordion-button:not(.collapsed)::after {
  filter: none;
  opacity: 1;
}

.offcanvas-menu .accordion-body {
  background: transparent;
  padding: 0 0 0.25rem;
}

/* Mobile submenu items */
.mobile-submenu-items,
.offcanvas-menu .submenu-items {
  display: flex;
  flex-direction: column;
  padding: 0 0 0 1rem;
  border-left: 2px solid rgba(0,0,0,0.08);
  margin-left: 1.2rem;
}

.mobile-subcategory-link {
  display: block;
  width: 100%;
  color: #444 !important;
  text-decoration: none !important;
  padding: 0.5rem 0.75rem !important;
  font-size: 0.82rem !important;
  border-radius: 6px;
  transition: background-color 0.15s ease, color 0.15s ease;
  border-bottom: none !important;
  background-color: transparent !important;
}

.mobile-subcategory-link:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
  color: #000 !important;
}

.subcategory-parent-link {
  font-weight: 600 !important;
  color: #222 !important;
}

.mobile-subcategory-children {
  display: flex !important;
  flex-direction: column !important;
  padding-left: 0.75rem !important;
  border-left: 1px solid rgba(0,0,0,0.07);
  margin-left: 0.5rem;
}

.mobile-grandchild-link {
  color: #666 !important;
  font-size: 0.78rem !important;
  text-decoration: none !important;
  padding: 0.35rem 0.6rem !important;
  border-radius: 5px;
  transition: color 0.15s ease, background-color 0.15s ease;
  border-bottom: none !important;
}

.mobile-grandchild-link:hover {
  color: #111 !important;
  background-color: rgba(0,0,0,0.04) !important;
}

.subcategory-with-children {
  display: flex;
  flex-direction: column;
  border-bottom: none !important;
}

/* Social links in footer */
.social-links-mobile {
  display: flex;
  gap: 0.75rem;
}

.social-link-mobile {
  color: #555 !important;
  text-decoration: none;
  font-size: 1.1rem;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.social-link-mobile:hover {
  background-color: var(--primary-color) !important;
  color: #fff !important;
}

.offcanvas-footer small {
  color: rgba(0,0,0,0.35) !important;
}

/* Info Links Styles */
.info-links {
  gap: 1.25rem;
}

.info-link {
  color: var(--text-dark);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
}

.info-link:hover {
  color: var(--primary-color);
}

.info-link i {
  font-size: 0.9rem;
}


/* Skeleton Loaders for Category Menu */
.skeleton-item {
  display: flex;
  align-items: center;
}

.skeleton-link {
  display: flex;
  align-items: center;
  width: 120px;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-icon {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.1);
  margin-right: 8px;
}

.skeleton-text {
  height: 14px;
  width: 80px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.mobile-skeleton-menu {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

.mobile-skeleton-item {
  padding: 8px 0;
}

.skeleton-button {
  height: 40px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.6;
  }
}

/* Resaltar más el menú activo */
.has-dropdown {
  position: relative;
}

.has-dropdown::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background-color: var(--primary-color);
  transition: width 0.3s ease;
}

.has-dropdown:hover::after,
.has-dropdown:focus-within::after {
  width: 100%;
}

/* Desktop Search Button */
.desktop-search-btn {
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.desktop-search-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.desktop-offcanvas-menu {
  position: fixed;
  top: 0;
  right: 0;
  width: 340px;
  height: 100vh;
  background-color: #fff;
  z-index: 1050;
  overflow-y: auto;
  transform: translateX(100%);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
}

.desktop-offcanvas-menu.show {
  transform: translateX(0);
}

.desktop-offcanvas-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  z-index: 1045;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.35s ease;
  pointer-events: none;
}

.desktop-offcanvas-backdrop.show {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.desktop-menu-item {
  margin-bottom: 2px;
}

.desktop-link {
  display: flex;
  align-items: center;
  padding: 0.75rem 0.9rem;
  color: #111 !important;
  text-decoration: none !important;
  font-weight: 500;
  font-size: 0.85rem;
  letter-spacing: 0.03em;
  border-radius: 8px;
  transition: background-color 0.2s ease, color 0.2s ease;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}

.desktop-link:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
  color: #000 !important;
}

.desktop-skeleton-menu {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 0;
}

.desktop-skeleton-item {
  padding: 4px 0;
}

/* Desktop accordion */
.desktop-offcanvas-menu .accordion-item {
  background: transparent;
  border: none !important;
  margin-bottom: 2px;
}

.desktop-offcanvas-menu .accordion-button {
  padding: 0.75rem 0.9rem;
  font-weight: 500;
  font-size: 0.85rem;
  letter-spacing: 0.03em;
  color: #111 !important;
  background-color: transparent !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  border: none !important;
  width: 100%;
  text-transform: none;
}

.desktop-offcanvas-menu .accordion-button:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
  color: #000 !important;
}

.desktop-offcanvas-menu .accordion-button:not(.collapsed) {
  color: var(--primary-color) !important;
  background-color: rgba(0, 0, 0, 0.04) !important;
  box-shadow: none !important;
}

.desktop-offcanvas-menu .accordion-button:focus {
  box-shadow: none;
  border-color: transparent;
}

.desktop-offcanvas-menu .accordion-button::after {
  filter: none;
  opacity: 0.5;
}

.desktop-offcanvas-menu .accordion-button:not(.collapsed)::after {
  opacity: 1;
  transform: rotate(-180deg);
}

.desktop-offcanvas-menu .accordion-body {
  background: transparent;
  padding: 0 0 0.25rem;
}

.desktop-offcanvas-menu .submenu-items {
  display: flex;
  flex-direction: column;
  padding: 0 0 0 0.75rem;
  border-left: 2px solid rgba(0,0,0,0.08);
  margin-left: 1.5rem;
}

.desktop-offcanvas-menu .submenu-link {
  display: block;
  padding: 0.5rem 0.75rem;
  color: #444 !important;
  text-decoration: none !important;
  font-size: 0.82rem;
  border-radius: 6px;
  transition: background-color 0.15s ease, color 0.15s ease;
  margin-bottom: 1px;
}

.desktop-offcanvas-menu .submenu-link:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #000 !important;
  padding-left: 1rem;
}

/* Estilos específicos para mobile */
@media (max-width: 991.98px) {
  /* already handled by the unified dark theme above */
}
</style>
